// The deals screen's changes view: what happened to the deals over a period,
// as one chart read left to right like the funnel.
//
// One read, `GET /api/reports/sales`. A column per stage from Lead to Ended,
// holding every deal that arrived there in the period: moved forward, new or
// reopened (tagged, in the stage it came in at), or moved back (marked). Lost
// deals are not drawn. Each column is stacked from its deals, or
// from each partner's deals together, coloured by partner, so the customers
// are read off the chart itself. A deal's tile links to it. A deal whose
// estimate only got more accurate is not drawn: that is not news.
//
// Of the screen's filters only the partner reaches this read; the rest narrow
// a list of deals as they are now, which is a different question.

import { Link } from "@tanstack/react-router";
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import type { SalesReport } from "@crm/shared";
import styles from "./DealsChanges.module.css";
import toolbarStyles from "./DealsList.module.css";
import { PeriodPicker } from "../../components/PeriodPicker.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { useSalesReport, type DealsQueryState } from "../../api/queries.js";
import { usePrincipal } from "../../auth/useAuth.js";
import { formatCalendarDateShort, formatMonth } from "../../format/calendarDate.js";
import { dealStateLabel } from "../../format/labels.js";
import { formatCompactMoney } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { amountOf, bucketFor, changeText, type Measure } from "../sales/salesModel.js";
import { useBoxSize } from "../sales/boardCharts.js";
import {
  changeChart,
  columnDeals,
  changeShortcuts,
  dealSize,
  goLiveShift,
  partnerSlots,
  pieces,
  type Chart,
  type ChartColumn,
  type ChartDeal,
  type ColumnGroup,
  type PartnerSlot,
  type PartnerSlots,
  type Piece,
  type Split,
} from "./changesModel.js";
import { PartnerSelect, VIEW_OPTIONS } from "./DealsToolbar.js";
import type { ChangesState, DealsViewMode } from "./dealsSearch.js";

const WEIGHTING: ReadonlyArray<SegmentedOption<"yes" | "no">> = [
  { value: "yes", label: "Weighted" },
  { value: "no", label: "Full value" },
];

const BASES: ReadonlyArray<SegmentedOption<"gross" | "our_share">> = [
  { value: "gross", label: "Gross" },
  { value: "our_share", label: "Our share" },
];

const SPLIT_OPTIONS: ReadonlyArray<SegmentedOption<Split>> = [
  { value: "deal", label: "By deal" },
  { value: "partner", label: "By partner" },
];

const GROUP_LABEL: Record<ColumnGroup, string> = {
  pipeline: "Pipeline",
  signed: "Signed",
};

/** The dot beside a column's name. */
function columnColour(column: ChartColumn): string {
  return column.group === "signed" ? "var(--series-1)" : "var(--brand)";
}

/** A deal's figure, in the tone of what happened to it: back a stage is the only bad news. */
function dealTone(deal: ChartDeal): string {
  return deal.change === "back" ? "var(--tone-warn-fg)" : "var(--tone-good-fg)";
}

export interface DealsChangesProps {
  state: DealsQueryState;
  onStateChange: (next: DealsQueryState) => void;
  view: DealsViewMode;
  onViewChange: (next: DealsViewMode) => void;
  today: string;
  changes: ChangesState;
  onChangesChange: (next: ChangesState) => void;
}

export function DealsChanges({
  state,
  onStateChange,
  view,
  onViewChange,
  today,
  changes,
  onChangesChange,
}: DealsChangesProps): ReactNode {
  const currency = useReportCurrency();
  const { period, measure } = changes;
  const report = useSalesReport({
    from: period.from,
    to: period.to,
    bucket: bucketFor(period, today),
    partnerId: state.partnerId,
    scope: "rolled_up",
    currency,
    coldDays: 30,
  });

  return (
    <div className={styles.root}>
      <div className={toolbarStyles.toolbar}>
        <Segmented
          label="Deals view"
          variant="inset"
          options={VIEW_OPTIONS}
          value={view}
          onChange={onViewChange}
        />
        <PeriodPicker
          period={period}
          today={today}
          shortcuts={changeShortcuts(today)}
          onChange={(next) => onChangesChange({ ...changes, period: next })}
        />
        <PartnerSelect
          value={state.partnerId}
          onChange={(partnerId) => onStateChange({ ...state, partnerId, page: 1 })}
        />
        <div className={styles.spacer} />
        <Segmented
          label="Weighting"
          variant="inset"
          options={WEIGHTING}
          value={measure.weighted ? "yes" : "no"}
          onChange={(value) =>
            onChangesChange({ ...changes, measure: { ...measure, weighted: value === "yes" } })
          }
        />
        <Segmented
          label="Value"
          variant="inset"
          options={BASES}
          value={measure.basis}
          onChange={(basis) => onChangesChange({ ...changes, measure: { ...measure, basis } })}
        />
      </div>

      <QueryBoundary query={report} label="deal changes">
        {(data) => (
          <Changes
            report={data}
            changes={changes}
            onChange={onChangesChange}
            refreshing={report.isPlaceholderData}
          />
        )}
      </QueryBoundary>
    </div>
  );
}

function Changes({
  report,
  changes,
  onChange,
  refreshing,
}: {
  report: SalesReport;
  changes: ChangesState;
  onChange: (next: ChangesState) => void;
  /** The figures are the last period's, shown while the new one loads. */
  refreshing: boolean;
}): ReactNode {
  const { measure, split } = changes;
  const money = (minor: number) => formatCompactMoney(minor, report.currency);
  const chart = useMemo(() => changeChart(report, measure), [report, measure]);
  const slots = useMemo(
    () =>
      partnerSlots(
        chart.columns.flatMap((column) => [...column.forward, ...column.back]),
        measure,
      ),
    [chart, measure],
  );
  // A partner picked in the legend, or by its piece, stands out in every column.
  const [focus, setFocus] = useState<string | null>(null);
  const picked = focus !== null && slots.slots.some((slot) => slot.id === focus) ? focus : null;
  const toggle = (id: string) => setFocus(picked === id ? null : id);
  const diff = chart.closing - chart.opening;
  const pct = changeText(chart.closing, chart.opening);

  return (
    <section
      className={`${styles.panel} ${refreshing ? styles.refreshing : ""}`}
      aria-label="Pipeline over the period"
      aria-busy={refreshing}
    >
      <div className={styles.head}>
        <div className={styles.headline}>
          <span className={styles.headLabel}>Pipeline</span>
          <span className={styles.headValue}>{money(chart.closing)}</span>
          <span className={diff > 0 ? styles.up : diff < 0 ? styles.down : styles.flat}>
            {diff === 0 ? "no change" : `${diff > 0 ? "+" : "−"}${money(Math.abs(diff))}`}
            {diff !== 0 && pct !== null && <span className={styles.pct}>{pct}</span>}
          </span>
          <span className={styles.headSub}>from {money(chart.opening)}</span>
        </div>
        <div className={styles.stats} aria-label="Movement">
          <span>
            <strong>{chart.fresh}</strong> new{" "}
            {chart.cameIn > 0 && <span className={styles.up}>+{money(chart.cameIn)}</span>}
          </span>
          <span>
            <strong>{chart.forward}</strong> forward · <strong>{chart.back}</strong> back
          </span>
        </div>
        <Segmented
          label="Stack by"
          variant="inset"
          options={SPLIT_OPTIONS}
          value={split}
          onChange={(next) => onChange({ ...changes, split: next })}
        />
      </div>

      {slots.slots.length > 0 && (
        <div className={styles.legend}>
          {slots.slots.map((slot) => (
            <button
              key={slot.id}
              type="button"
              className={`${styles.legendItem} ${picked !== null && picked !== slot.id ? styles.legendDim : ""}`}
              aria-pressed={picked === slot.id}
              onClick={() => toggle(slot.id)}
            >
              <span
                className={styles.swatch}
                style={{ background: slot.colour }}
                aria-hidden="true"
              />
              {slot.name}
            </button>
          ))}
        </div>
      )}

      <Funnel
        chart={chart}
        measure={measure}
        split={split}
        slots={slots}
        focus={picked}
        onFocus={toggle}
        money={money}
      />
    </section>
  );
}

// ------------------------------------------------------------------- funnel

/** The stacks stand on a line near the bottom; under it, each column's name, then each part's caption. */
const MARGIN = { top: 8, right: 4, bottom: 62, left: 4 };
/** Between two tiles, so neighbours never read as one shape. */
const PIECE_GAP = 2;
/** The tallest a deal's tile is drawn, and the shortest that still holds its name. */
const MAX_TILE = 44;
const MIN_TILE = 18;
/** Room above a stack for its total. */
const LABEL_ROOM = 24;
const MAX_BAR = 240;
const COLUMN_GAP = 10;
/** Between the funnel's parts, on top of the gap between columns. */
const GROUP_GAP = 26;
/** An empty stage keeps its place in the funnel at this share of a busy one's width. */
const EMPTY_WEIGHT = 0.55;
/** Before the box is measured (and in tests) the chart is drawn at this size. */
const FALLBACK = { width: 1100, height: 460 };

/** How a piece's deals arrived: each kind but a move forward is marked on the tile. */
type Arrival = "forward" | "fresh" | "back";

interface Placed {
  readonly piece: Piece;
  readonly arrival: Arrival;
  readonly top: number;
  readonly height: number;
}

interface Slot {
  readonly left: number;
  readonly width: number;
  readonly barLeft: number;
  readonly barWidth: number;
}

/**
 * The funnel in real pixels, one tile per deal, every column standing on one
 * line near the bottom, the most valuable deals nearest it. A busy column is
 * wider than an empty one. The tile is as tall as the fullest column allows;
 * when even the shortest readable tile cannot hold every deal, a column shows
 * what fits and folds the rest into one tile.
 */
function Funnel({
  chart,
  measure,
  split,
  slots,
  focus,
  onFocus,
  money,
}: {
  chart: Chart;
  measure: Measure;
  split: Split;
  slots: PartnerSlots;
  focus: string | null;
  onFocus: (id: string) => void;
  money: (minor: number) => string;
}): ReactNode {
  const [box, measured] = useBoxSize();
  const size = measured.width > 0 && measured.height > 0 ? measured : FALLBACK;
  const [hover, setHover] = useState<{ column: number; key: string } | null>(null);
  const { columns } = chart;
  const plotWidth = Math.max(0, size.width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(1, size.height - MARGIN.top - MARGIN.bottom);
  const baseline = MARGIN.top + plotHeight;

  // Widths: a busy column gets a full share, an empty one a narrow slot.
  const weights = columns.map((c) => (columnDeals(c).length === 0 ? EMPTY_WEIGHT : 1));
  const groupBreaks = columns.filter((c, i) => i > 0 && c.group !== columns[i - 1]?.group).length;
  const gaps = COLUMN_GAP * Math.max(0, columns.length - 1) + GROUP_GAP * groupBreaks;
  const unit =
    Math.max(0, plotWidth - gaps) /
    Math.max(
      1,
      weights.reduce((a, b) => a + b, 0),
    );
  const slotsAt: Slot[] = [];
  let x = MARGIN.left;
  columns.forEach((column, i) => {
    if (i > 0) x += COLUMN_GAP + (column.group !== columns[i - 1]?.group ? GROUP_GAP : 0);
    const width = unit * (weights[i] ?? 1);
    const barWidth = Math.min(MAX_BAR, width);
    slotsAt.push({ left: x, width, barLeft: x + (width - barWidth) / 2, barWidth });
    x += width;
  });

  // Heights: one tile per deal.
  const most = Math.max(0, ...columns.map((c) => c.forward.length + c.back.length));
  const room = plotHeight - LABEL_ROOM;
  const fitted = room / Math.max(1, most);
  const tile = Math.min(MAX_TILE, split === "deal" ? Math.max(MIN_TILE, fitted) : fitted);
  const capacity = Math.max(1, Math.floor(room / tile));

  // Moves forward nearest the line, then new deals, then moves back on top.
  // When a column holds more than fits, each kind keeps a share of the room.
  const placedAt = columns.map((column) => {
    const total = columnDeals(column).length;
    const share = (deals: readonly ChartDeal[]) =>
      deals.length === 0
        ? 0
        : total <= capacity
          ? deals.length
          : Math.max(1, Math.round((capacity * deals.length) / total));
    const backCap = share(column.back);
    const freshCap = share(column.fresh);
    const runs: ReadonlyArray<{ deals: readonly ChartDeal[]; arrival: Arrival; cap: number }> = [
      {
        deals: column.forward,
        arrival: "forward",
        cap: Math.max(1, capacity - backCap - freshCap),
      },
      { deals: column.fresh, arrival: "fresh", cap: freshCap },
      { deals: column.back, arrival: "back", cap: backCap },
    ];
    let offset = 0;
    return runs.flatMap((run) =>
      pieces(run.deals, measure, split, slots, run.cap).map((piece): Placed => {
        const height = piece.tiles * tile;
        offset += height;
        return {
          piece: { ...piece, key: `${run.arrival}:${piece.key}` },
          arrival: run.arrival,
          top: baseline - offset,
          height,
        };
      }),
    );
  });

  const groups = columns.flatMap((column, i) =>
    i > 0 && columns[i - 1]?.group === column.group ? [] : [{ group: column.group, first: i }],
  );
  const hovered =
    hover === null
      ? undefined
      : placedAt[hover.column]?.find((placed) => placed.piece.key === hover.key);
  const hoveredSlot = hover === null ? undefined : slotsAt[hover.column];

  return (
    <div className={styles.plot} ref={box} onMouseLeave={() => setHover(null)}>
      <div
        className={styles.baseline}
        style={{ top: baseline, left: MARGIN.left, right: MARGIN.right }}
        aria-hidden="true"
      />

      {columns.map((column, index) => {
        const slot = slotsAt[index];
        const placed = placedAt[index] ?? [];
        if (slot === undefined) return null;
        const stackTop = Math.min(baseline, ...placed.map((p) => p.top));
        return (
          <div key={column.key} role="group" aria-label={columnName(column, money)}>
            {column.value > 0 && (
              <span
                className={styles.columnValue}
                style={{ left: slot.barLeft, width: slot.barWidth, top: stackTop - 21 }}
                aria-hidden="true"
              >
                {money(column.value)}
              </span>
            )}

            {placed.map(({ piece, arrival, top, height }) => (
              <PieceBox
                key={piece.key}
                piece={piece}
                arrival={arrival}
                measure={measure}
                money={money}
                box={{
                  left: slot.barLeft,
                  width: slot.barWidth,
                  top: top + PIECE_GAP,
                  height: Math.max(1, height - PIECE_GAP),
                }}
                dim={focus !== null && piece.slot?.id !== focus}
                onHover={() => setHover({ column: index, key: piece.key })}
                onFocus={onFocus}
              />
            ))}

            <span
              className={styles.columnName}
              style={{ left: slot.left, width: slot.width, top: baseline + 7 }}
            >
              <span className={styles.nameLine} title={column.label}>
                <span
                  className={styles.stepDot}
                  style={{ background: columnColour(column) }}
                  aria-hidden="true"
                />
                <span className={styles.nameText}>{column.label}</span>
              </span>
              <span className={styles.nameSub}>{columnSub(column)}</span>
            </span>
          </div>
        );
      })}

      {groups.map(({ group, first }) => {
        const members = columns.filter((column) => column.group === group).length;
        const start = slotsAt[first];
        const end = slotsAt[first + members - 1];
        if (start === undefined || end === undefined) return null;
        return (
          <span
            key={group}
            className={styles.groupLabel}
            style={{
              left: start.left,
              width: end.left + end.width - start.left,
              top: baseline + 44,
            }}
          >
            {GROUP_LABEL[group]}
          </span>
        );
      })}

      {hovered !== undefined && hoveredSlot !== undefined && (
        <PieceTip
          placed={hovered}
          measure={measure}
          money={money}
          barLeft={hoveredSlot.barLeft}
          barWidth={hoveredSlot.barWidth}
          width={size.width}
          height={size.height}
        />
      )}
    </div>
  );
}

/** "Proposal: 3 forward · 1 new, $410k", for a reader that cannot see the tiles. */
function columnName(column: ChartColumn, money: (minor: number) => string): string {
  if (columnDeals(column).length === 0) return `${column.label}: none`;
  return `${column.label}: ${columnSub(column)}, ${money(column.value)}`;
}

/** "3 forward · 1 new · 1 back", each part only when there is one. */
function columnSub(column: ChartColumn): string {
  const parts = [
    column.forward.length > 0 ? `${column.forward.length} forward` : null,
    column.fresh.length > 0 ? `${column.fresh.length} new` : null,
    column.back.length > 0 ? `${column.back.length} back` : null,
  ].filter((part) => part !== null);
  return parts.length === 0 ? "–" : parts.join(" · ");
}

/** What a piece is worth now; null for a deal nobody has estimated yet. */
function pieceFigure(
  piece: Piece,
  measure: Measure,
  money: (minor: number) => string,
): string | null {
  const total = piece.deals.reduce((sum, deal) => sum + dealSize(deal, measure), 0);
  return total === 0 ? null : money(total);
}

/** The tag on a new or reopened deal's tile. */
function freshTag(piece: Piece): string {
  return piece.deals.every((deal) => deal.change === "reopened") ? "reopened" : "new";
}

/**
 * One piece of a column: a deal links to it, a partner picks itself out, and
 * the rest only tells what is in it. Its name is drawn inside it when it is
 * tall enough to read, its figure at the right when wide enough. A new or
 * reopened deal is tagged; a deal that moved back a stage is marked.
 */
function PieceBox({
  piece,
  arrival,
  measure,
  money,
  box,
  dim,
  onHover,
  onFocus,
}: {
  piece: Piece;
  arrival: Arrival;
  measure: Measure;
  money: (minor: number) => string;
  box: { left: number; top: number; width: number; height: number };
  dim: boolean;
  onHover: () => void;
  onFocus: (id: string) => void;
}): ReactNode {
  const colour = piece.slot?.colour ?? "var(--series-other)";
  const figure = pieceFigure(piece, measure, money);
  const className = [
    styles.piece,
    dim ? styles.dim : "",
    piece.kind === "rest" ? styles.rest : "",
    arrival === "back" ? styles.back : "",
  ].join(" ");
  const style = { ...box, "--piece": colour } as CSSProperties;
  // A tall tile has room for whose deal it is under its name.
  const sub = piece.kind === "deal" && box.height >= 32 ? piece.deals[0]?.partner_name : undefined;
  const content = box.height >= 15 && (
    <>
      <span className={styles.pieceLine}>
        {arrival === "back" && (
          <span className={styles.backMark} aria-hidden="true">
            ↩
          </span>
        )}
        {arrival === "fresh" && <span className={styles.newTag}>{freshTag(piece)}</span>}
        <span className={styles.pieceName}>{piece.label}</span>
        {box.width >= 110 && figure !== null && (
          <span className={styles.pieceFigure}>{figure}</span>
        )}
      </span>
      {sub !== undefined && <span className={styles.pieceSub}>{sub}</span>}
    </>
  );
  const how =
    arrival === "back" ? ", moved back" : arrival === "fresh" ? `, ${freshTag(piece)}` : "";
  const name = `${piece.label}${how}${figure === null ? "" : `, ${figure}`}`;

  if (piece.kind === "deal") {
    const deal = piece.deals[0];
    if (deal === undefined) return null;
    return (
      <Link
        to="/deals/$dealId"
        params={{ dealId: deal.deal_id }}
        className={className}
        style={style}
        aria-label={name}
        onMouseEnter={onHover}
        onFocus={onHover}
      >
        {content}
      </Link>
    );
  }
  if (piece.kind === "partner" && piece.slot !== null) {
    const slot: PartnerSlot = piece.slot;
    return (
      <button
        type="button"
        className={className}
        style={style}
        aria-label={name}
        onMouseEnter={onHover}
        onFocus={onHover}
        onClick={() => onFocus(slot.id)}
      >
        {content}
      </button>
    );
  }
  return (
    <span
      className={className}
      style={style}
      role="img"
      aria-label={name}
      tabIndex={0}
      onMouseEnter={onHover}
      onFocus={onHover}
    >
      {content}
    </span>
  );
}

const TIP_WIDTH = 270;
const TIP_ROWS = 8;

/** What a hovered piece holds, beside the bar on whichever side has room. */
function PieceTip({
  placed,
  measure,
  money,
  barLeft,
  barWidth,
  width,
  height,
}: {
  placed: Placed;
  measure: Measure;
  money: (minor: number) => string;
  barLeft: number;
  barWidth: number;
  width: number;
  height: number;
}): ReactNode {
  const { piece } = placed;
  const right = barLeft + barWidth + 10;
  const left = right + TIP_WIDTH > width ? barLeft - 10 - TIP_WIDTH : right;
  const top = Math.max(4, Math.min(placed.top, height - 190));
  const deal = piece.kind === "deal" ? piece.deals[0] : undefined;
  return (
    <div className={styles.tip} style={{ left, top, width: TIP_WIDTH }} role="tooltip">
      {deal !== undefined ? (
        <DealDetail deal={deal} measure={measure} money={money} />
      ) : (
        <>
          <div className={styles.tipHead}>
            <span className={styles.tipTitle}>
              {piece.slot !== null && (
                <span
                  className={styles.swatch}
                  style={{ background: piece.slot.colour }}
                  aria-hidden="true"
                />
              )}
              {piece.kind === "partner" ? piece.label : dealCount(piece.deals.length)}
            </span>
            <span className={styles.tipFigure}>{pieceFigure(piece, measure, money) ?? ""}</span>
          </div>
          {[...piece.deals]
            .sort((a, b) => dealSize(b, measure) - dealSize(a, measure))
            .slice(0, TIP_ROWS)
            .map((d) => (
              <div key={d.deal_id} className={styles.tipRow}>
                <span className={styles.tipName}>{d.customer_name}</span>
                <span className={styles.tipValue}>{money(dealSize(d, measure))}</span>
              </div>
            ))}
          {piece.deals.length > TIP_ROWS && (
            <div className={`${styles.tipRow} ${styles.faint}`}>
              {`${piece.deals.length - TIP_ROWS} more`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DealDetail({
  deal,
  measure,
  money,
}: {
  deal: ChartDeal;
  measure: Measure;
  money: (minor: number) => string;
}): ReactNode {
  const shift = goLiveShift(deal);
  const stateNow = deal.state_to;
  const size = money(dealSize(deal, measure));
  const came = deal.change === "new" || deal.change === "reopened";
  return (
    <>
      <div className={styles.tipHead}>
        <span className={styles.tipTitle}>{deal.customer_name}</span>
        <span className={styles.tipFigure} style={{ color: dealTone(deal) }}>
          {came ? `+${size}` : size}
        </span>
      </div>
      <div className={styles.tipSub}>{deal.partner_name}</div>
      <dl className={styles.facts}>
        <dt>Stage</dt>
        <dd>
          {deal.state_from !== null && deal.state_from !== stateNow && (
            <span className={styles.faint}>{dealStateLabel(deal.state_from)} → </span>
          )}
          {dealStateLabel(stateNow)}
        </dd>
        {deal.from !== null && (
          <>
            <dt>Value</dt>
            <dd>
              {money(amountOf(deal.from, measure))} → {money(amountOf(deal.to, measure))}
            </dd>
          </>
        )}
        {shift !== null && (
          <>
            <dt>Go-live</dt>
            <dd className={shift.later ? styles.later : styles.sooner}>
              {monthMove(shift.from, shift.to)}
            </dd>
          </>
        )}
        {deal.last_changed_at !== null && (
          <>
            <dt>Changed</dt>
            <dd>
              {formatCalendarDateShort(deal.last_changed_at.slice(0, 10))}
              <Who deal={deal} />
            </dd>
          </>
        )}
      </dl>
    </>
  );
}

function dealCount(count: number): string {
  return `${count} ${count === 1 ? "deal" : "deals"}`;
}

/** "Mar → May 2027", "Dec 2026 → Feb 2027", or "May 2027" when it was not set before. */
function monthMove(from: string | null, to: string): string {
  if (from === null) return formatMonth(to);
  if (from.slice(0, 4) === to.slice(0, 4)) {
    return `${formatMonth(from).split(" ")[0] ?? from} → ${formatMonth(to)}`;
  }
  return `${formatMonth(from)} → ${formatMonth(to)}`;
}

function Who({ deal }: { deal: ChartDeal }): ReactNode {
  const principal = usePrincipal();
  switch (deal.last_changed_by) {
    case null:
      return null;
    case "import":
      return <span className={styles.faint}> · HubSpot</span>;
    case "agent":
      return <span className={styles.faint}> · Agent</span>;
    case "user":
      return deal.last_changed_by_subject !== null &&
        deal.last_changed_by_subject === principal.subject ? (
        <span className={styles.faint}> · You</span>
      ) : null;
  }
}
